import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import firebase from 'firebase/compat/app';
import { catchError, combineLatest, map, Observable, of } from 'rxjs';
import { IUserDevice, IUserDeviceCommand, UserDeviceCapability, UserDeviceType } from '@platfio/interfaces/platfio';

type FirebaseErrorLike = {
  code?: string;
};

export interface UserDeviceUpsertInput {
  deviceId: string;
  name: string;
  type: UserDeviceType;
  capabilities: UserDeviceCapability[];
  platform: string | null;
  clientVersion?: string | null;
  agencyId?: string | null;
  agencyShared?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class UserDeviceService {
  constructor(private afs: AngularFirestore) {}

  getDevices(uid: string): Observable<IUserDevice[]> {
    return this.afs.collection<IUserDevice>(`users/${uid}/devices`, ref =>
      ref.orderBy('updatedAt', 'desc')
    ).valueChanges({ idField: 'deviceId' as never }) as Observable<IUserDevice[]>;
  }

  getAccessibleDevices(uid: string, agencyId?: string | null): Observable<IUserDevice[]> {
    const normalizedAgencyId = normalizeAgencyId(agencyId, uid);
    if (!normalizedAgencyId) {
      return this.getDevices(uid);
    }

    const ownDevices$ = this.getDevices(uid);
    const sharedDevices$ = this.afs.collectionGroup<IUserDevice>('devices', ref =>
      ref
        .where('agencyId', '==', normalizedAgencyId)
        .where('agencyShared', '==', true)
    ).valueChanges({ idField: 'deviceId' as never }).pipe(
      catchError((error) => {
        if (!isPermissionDeniedError(error)) {
          console.warn('Falling back to own devices after shared device lookup failed.', error);
        }

        return of([]);
      })
    ) as Observable<IUserDevice[]>;

    return combineLatest([ownDevices$, sharedDevices$]).pipe(
      map(([ownDevices, sharedDevices]) => mergeDevices(ownDevices || [], sharedDevices || []))
    );
  }

  getDeviceCommands(uid: string, deviceId: string): Observable<IUserDeviceCommand[]> {
    return this.afs.collection<IUserDeviceCommand>(`users/${uid}/devices/${deviceId}/commands`, ref =>
      ref.orderBy('time', 'desc')
    ).valueChanges({ idField: 'commandId' as never }) as Observable<IUserDeviceCommand[]>;
  }

  async upsertDevice(uid: string, input: UserDeviceUpsertInput): Promise<void> {
    const ref = this.afs.doc(`users/${uid}/devices/${input.deviceId}`);
    const existing = await ref.get().toPromise();
    const now = firebase.firestore.FieldValue.serverTimestamp();
    const agencyId = normalizeAgencyId(input.agencyId, uid);

    await ref.set({
      deviceId: input.deviceId,
      userId: uid,
      agencyId,
      agencyShared: input.agencyShared === true,
      name: input.name,
      type: input.type,
      capabilities: input.capabilities,
      platform: input.platform || '',
      clientVersion: input.clientVersion || null,
      registeredAt: existing?.exists ? existing.get('registeredAt') || now : now,
      lastSeenAt: now,
      updatedAt: now
    }, { merge: true });
  }

  async touchDeviceHeartbeat(uid: string, deviceId: string): Promise<void> {
    const now = firebase.firestore.FieldValue.serverTimestamp();
    await this.afs.doc(`users/${uid}/devices/${deviceId}`).set({
      lastSeenAt: now,
      updatedAt: now
    }, { merge: true });
  }

  async deleteDevice(uid: string, deviceId: string): Promise<void> {
    const deviceRef = this.afs.doc(`users/${uid}/devices/${deviceId}`).ref;
    const commandsCollection = deviceRef.collection('commands');

    while (true) {
      const snapshot = await commandsCollection.limit(200).get();
      if (snapshot.empty) {
        break;
      }

      const batch = this.afs.firestore.batch();
      snapshot.docs.forEach((commandDoc) => batch.delete(commandDoc.ref));
      await batch.commit();
    }

    await deviceRef.delete();
  }

  async updateDeviceCommand(
    uid: string,
    deviceId: string,
    commandId: string,
    updates: Record<string, unknown>
  ): Promise<void> {
    const now = firebase.firestore.FieldValue.serverTimestamp();
    await this.afs.doc(`users/${uid}/devices/${deviceId}/commands/${commandId}`).set({
      ...stripUndefinedValues(updates),
      updatedAt: now
    }, { merge: true });
  }
}

function stripUndefinedValues(value: Record<string, unknown>): Record<string, unknown> {
  return Object.entries(value).reduce<Record<string, unknown>>((accumulator, [key, currentValue]) => {
    if (currentValue !== undefined) {
      accumulator[key] = currentValue;
    }

    return accumulator;
  }, {});
}

function normalizeAgencyId(agencyId: string | null | undefined, fallbackUid: string): string {
  const normalizedAgencyId = String(agencyId || '').trim();
  return normalizedAgencyId || fallbackUid;
}

function mergeDevices(ownDevices: IUserDevice[], sharedDevices: IUserDevice[]): IUserDevice[] {
  const deviceById = new Map<string, IUserDevice>();

  for (const device of [...ownDevices, ...sharedDevices]) {
    const deviceKey = getDeviceKey(device);
    if (!deviceById.has(deviceKey)) {
      deviceById.set(deviceKey, device);
    }
  }

  return Array.from(deviceById.values()).sort((left, right) => getDateValue(right.updatedAt) - getDateValue(left.updatedAt));
}

function getDateValue(value: unknown): number {
  if (!value) {
    return 0;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? 0 : value.getTime();
  }

  if (typeof (value as { toDate?: () => Date }).toDate === 'function') {
    const date = (value as { toDate: () => Date }).toDate();
    return date instanceof Date && !Number.isNaN(date.getTime()) ? date.getTime() : 0;
  }

  const parsed = new Date(String(value));
  return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
}

function getDeviceKey(device: IUserDevice): string {
  return `${String(device.userId || '').trim()}:${String(device.deviceId || '').trim()}`;
}

function isPermissionDeniedError(error: unknown): boolean {
  const code = String((error as FirebaseErrorLike | null | undefined)?.code || '').trim().toLowerCase();
  return code === 'permission-denied' || code === 'firestore/permission-denied';
}